import { useState } from "react";
import api from "../api";
import { Field, FileInput, Actions, PrimaryButton, ResultBanner, BusyNote, ProgressBar, CardIntro } from "./ui/Form";

interface Props {
    layerType: "surveillance" | "disease";
}

const CONFIG = {
    surveillance: {
        label: "Collections CSV",
        intro: "Upload the raw collections CSV exported from VectorSurv. It replaces the data behind the internal surveillance dashboard.",
        source: "VectorSurv → Collections → Export",
        syncNote: "Pulls this year's collections from VectorSurv and replaces the Collection 2026 layer. No download, no upload.",
    },
    disease: {
        label: "Pools CSV",
        intro: "Upload the latest pools CSV from VectorSurv. It replaces the data behind the public disease monitoring dashboard.",
        source: "VectorSurv → Pools → Export",
        syncNote: "Pulls this year's pools and their test results from VectorSurv and replaces the Pools 2026 layer. No download, no upload.",
    },
} as const;

export default function UpdateLayerCard({ layerType }: Props) {
    const [file, setFile] = useState<File | null>(null);
    const [result, setResult] = useState<any>(null);
    const [busy, setBusy] = useState<"" | "upload" | "sync">("");

    const config = CONFIG[layerType];

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!file) {
            setResult({ status: "failure", message: "Choose a CSV file first." });
            return;
        }

        setBusy("upload");
        setResult(null);

        const formData = new FormData();
        formData.append("csv_update", file);
        formData.append("layer_type", layerType);

        try {
            const response = await api.post("/update-layer", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResult({ status: "success", ...response.data });
        } catch (err: any) {
            setResult(err.response?.data || { status: "failure", message: err.message });
        } finally {
            setBusy("");
        }
    };

    /** Same destination and the same delete-and-add as the upload; only the
     *  source changes. Confirmed first because it empties the layer before
     *  the new rows go in: an interrupted sync leaves the dashboard short,
     *  and that is worth one click to acknowledge rather than discover. */
    const syncFromVectorsurv = async () => {
        const ok = window.confirm(
            `This replaces every row in the ${layerType === "disease" ? "Pools 2026" : "Collection 2026"} layer ` +
            `with what VectorSurv holds right now.\n\nThe layer is emptied before the new rows are written, so if the ` +
            `pull fails part way the dashboard will be short until it is run again.\n\nContinue?`
        );
        if (!ok) return;

        setBusy("sync");
        setResult(null);
        try {
            const response = await api.post("/sync-dashboard", { layer_type: layerType });
            setResult({ status: "success", ...response.data });
        } catch (err: any) {
            setResult(err.response?.data || { status: "failure", message: err.message });
        } finally {
            setBusy("");
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <CardIntro>{config.intro}</CardIntro>

            <Field label={config.label} hint={config.source}>
                <FileInput accept=".csv" file={file} onFile={setFile} disabled={!!busy} />
            </Field>

            <Actions>
                <PrimaryButton type="submit" busy={busy === "upload"} busyLabel="Uploading…" disabled={!file || !!busy}>
                    Update dashboard
                </PrimaryButton>
                <span className="text-ink-faint text-[13px] px-1">or</span>
                <button
                    type="button"
                    onClick={syncFromVectorsurv}
                    disabled={!!busy}
                    className="btn-ghost text-sm"
                    title={config.syncNote}
                >
                    {busy === "sync" ? "Syncing from VectorSurv…" : "Sync AGOL"}
                </button>
            </Actions>

            <p className="text-[12px] text-ink-faint">{config.syncNote}</p>

            {busy && <ProgressBar />}
            {busy === "upload" && (
                <BusyNote>
                    Uploading and republishing the layer. Large exports can take a minute.
                </BusyNote>
            )}
            {busy === "sync" && (
                <BusyNote>
                    Reading VectorSurv, then replacing the layer. This takes a minute or two —
                    leave the page open.
                </BusyNote>
            )}

            <ResultBanner result={result} />
        </form>
    );
}
